import { useRouter } from "@tanstack/react-router";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flag,
  Info,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { JobTypeChip } from "@/components/ui/JobTypeChip";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { TypeChip } from "@/components/ui/TypeChip";
import {
  type TimeEntry,
  useDeleteTimeEntry,
  useSubmitWeek,
  useWeeklySummary,
} from "@/hooks/use-time-entries";
import { useSession } from "@/lib/auth-client";
import { OVERHEAD } from "@/lib/usg-constants";

function getCurrentWeekMonday() {
  const today = new Date();
  const dow = today.getDay();
  const monOffset = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(today);
  mon.setDate(today.getDate() + monOffset);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function getWeekDays(mon: Date) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon);
    d.setDate(mon.getDate() + i);
    return d;
  });
}

function formatHours(n: number) {
  return (Math.round(n * 10) / 10).toFixed(1);
}

function fmtDay(d: Date) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// ---- KPI Tile ----
interface KpiProps {
  label: string;
  value: string | number;
  unit?: string;
  accent?: string;
  flagged?: boolean;
}

function Kpi({ label, value, unit, accent, flagged }: KpiProps) {
  return (
    <div className={`kpi${flagged ? " kpi--flagged" : ""}`}>
      <div className="kpi__label">
        {flagged && <Flag size={12} />}
        {label}
      </div>
      <div className="kpi__value">
        {value}
        {unit && <span className="kpi__unit">{unit}</span>}
      </div>
      {!flagged && (
        <div className="kpi__accent" style={accent ? { background: accent } : undefined} />
      )}
      {flagged && <div className="kpi__accent" />}
    </div>
  );
}

// ---- Entry Row ----
interface EntryRowProps {
  entry: TimeEntry;
  onEdit: (entry: TimeEntry) => void;
}

function EntryRow({ entry, onEdit }: EntryRowProps) {
  const isOverhead = entry.type === "overhead";
  const oh = isOverhead ? OVERHEAD.find((o) => o.code === entry.overheadCode) : null;

  const primary = isOverhead
    ? `${oh?.code ?? entry.overheadCode} · ${oh?.name ?? ""}`
    : (entry.woNumber ?? "");

  const secondary = isOverhead ? (entry.overheadSub ?? "—") : "";

  const categoryText = isOverhead
    ? entry.overheadSub
      ? `Overhead › ${entry.overheadSub}`
      : "Overhead"
    : `${entry.category ?? ""} › ${entry.subtask ?? ""}`;

  const locked = entry.status === "submitted" || entry.status === "approved";
  const canEdit = !locked || entry.status === "flagged";
  const canDelete = entry.status === "draft";

  const deleteEntry = useDeleteTimeEntry();

  function handleDelete() {
    if (!confirm(`Delete this time entry? This cannot be undone.`)) return;
    deleteEntry.mutate(entry.id);
  }

  return (
    <div className={`entry-row${entry.status === "flagged" ? " is-flagged" : ""}`}>
      <div className="entry-row__wo">
        <span className={`entry-row__wo-primary${isOverhead ? " is-overhead" : ""}`}>
          {primary}
        </span>
        {secondary && <span className="entry-row__wo-secondary">{secondary}</span>}
      </div>
      <div className="entry-row__category" title={categoryText}>
        {categoryText}
      </div>
      <div className="entry-row__hours">{formatHours(entry.hours)} hrs</div>
      <div>
        {isOverhead ? <TypeChip type="overhead" /> : <JobTypeChip type={entry.category ?? ""} />}
      </div>
      <div className="entry-row__status-cell">
        <StatusBadge status={entry.status as "draft" | "submitted" | "approved" | "flagged"} />
        {entry.status === "flagged" && entry.flagManager && (
          <span className="flag-info">
            <Info size={14} />
            <span className="flag-info__tip">
              <strong>Flagged by {entry.flagManager}</strong>
              {entry.flagNote}
            </span>
          </span>
        )}
      </div>
      <div className="entry-row__actions">
        <button
          type="button"
          className="icon-btn"
          title={canEdit ? "Edit entry" : "Locked — awaiting review"}
          disabled={!canEdit}
          onClick={() => onEdit(entry)}
        >
          <Pencil size={15} />
        </button>
        <button
          type="button"
          className="icon-btn icon-btn--danger"
          title={canDelete ? "Delete entry" : "Cannot delete"}
          disabled={!canDelete || deleteEntry.isPending}
          onClick={handleDelete}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ---- Day Group ----
interface DayGroupProps {
  date: Date;
  entries: TimeEntry[];
  onEdit: (entry: TimeEntry) => void;
}

function DayGroup({ date, entries, onEdit }: DayGroupProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="entry-day">
      <button type="button" className="entry-day__header" onClick={() => setCollapsed((c) => !c)}>
        <div className="entry-day__date">
          {collapsed ? (
            <ChevronRight size={14} color="#6B7280" />
          ) : (
            <ChevronDown size={14} color="#6B7280" />
          )}
          <span>{fmtDay(date)}</span>
        </div>
      </button>
      {!collapsed && (
        <div style={{ paddingLeft: 24 }}>
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Empty State ----
function EmptyState() {
  return (
    <div style={{ padding: "64px 24px", textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          margin: "0 auto 16px",
          borderRadius: "50%",
          background: "var(--surface-alt)",
          display: "grid",
          placeItems: "center",
          color: "var(--muted)",
        }}
      >
        <Clock size={28} />
      </div>
      <h3 className="h-section" style={{ marginBottom: 6 }}>
        No time logged this week
      </h3>
      <p className="t-muted" style={{ marginBottom: 4 }}>
        Log your first entry to get started.
      </p>
    </div>
  );
}

// ---- Submit Bar ----
interface SubmitBarProps {
  hasDrafts: boolean;
  hasFlagged: boolean;
  draftCount: number;
  onClick: () => void;
  onLogTime: () => void;
}

function SubmitBar({ hasDrafts, hasFlagged, draftCount, onClick, onLogTime }: SubmitBarProps) {
  if (hasFlagged) {
    return (
      <div className="submit-bar">
        <div className="submit-bar__text">
          <strong>Resolve flagged entries before submitting</strong>
          <p>Edit each flagged entry above, correct the issue, and save to resubmit.</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" className="btn btn--primary btn--lg" onClick={onLogTime}>
            <Plus size={16} className="btn__icon" /> Log Time
          </button>
          <button type="button" className="btn btn--secondary" disabled>
            Submit Week for Review
          </button>
        </div>
      </div>
    );
  }
  if (!hasDrafts) {
    return (
      <div className="submit-bar">
        <div className="submit-bar__text">
          <strong>Week submitted</strong>
          <p>Awaiting manager review. You&apos;ll be notified if any entry is flagged.</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" className="btn btn--primary btn--lg" onClick={onLogTime}>
            <Plus size={16} className="btn__icon" /> Log Time
          </button>
          <button type="button" className="btn btn--secondary" disabled>
            Submit Week for Review
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="submit-bar">
      <div className="submit-bar__text">
        <strong>
          {draftCount} draft {draftCount === 1 ? "entry" : "entries"} ready
        </strong>
        <p>Review your week, then submit for manager approval.</p>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" className="btn btn--primary btn--lg" onClick={onLogTime}>
          <Plus size={16} className="btn__icon" /> Log Time
        </button>
        <button type="button" className="btn btn--secondary" onClick={onClick}>
          Submit Week for Review
        </button>
      </div>
    </div>
  );
}

// ---- Submit Confirmation Modal ----
interface SubmitModalProps {
  open: boolean;
  onClose: () => void;
  draftCount: number;
  draftBillable: number;
  draftOverhead: number;
  weekRange: string;
  onConfirm: () => void;
  isPending: boolean;
}

function SubmitModal({
  open,
  onClose,
  draftCount,
  draftBillable,
  draftOverhead,
  weekRange,
  onConfirm,
  isPending,
}: SubmitModalProps) {
  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">Submit week for review?</h2>
        </div>
        <div className="modal__body">
          <p style={{ margin: "0 0 6px" }}>
            You are submitting{" "}
            <strong>
              {draftCount} {draftCount === 1 ? "entry" : "entries"}
            </strong>{" "}
            totalling <strong>{formatHours(draftBillable + draftOverhead)} hours</strong> for the
            week of <strong>{weekRange}</strong>.
          </p>
          <div className="modal__summary">
            <div className="modal__summary-row">
              <span>Billable</span>
              <strong>{formatHours(draftBillable)} hrs</strong>
            </div>
            <div className="modal__summary-row">
              <span>Overhead</span>
              <strong>{formatHours(draftOverhead)} hrs</strong>
            </div>
            <div className="modal__summary-row">
              <span>Total</span>
              <strong>{formatHours(draftBillable + draftOverhead)} hrs</strong>
            </div>
          </div>
          <p style={{ color: "var(--muted)", fontSize: 13, margin: 0 }}>
            Once submitted, your entries will be locked and sent to your manager for approval.
          </p>
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? "Submitting…" : "Submit for Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Main Dashboard Page ----
export function DashboardPage() {
  const router = useRouter();
  const { data: session } = useSession();
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [selectedMonday, setSelectedMonday] = useState(getCurrentWeekMonday);

  const currentMonday = getCurrentWeekMonday();
  const isCurrentWeek = selectedMonday.getTime() === currentMonday.getTime();

  function navigateWeek(delta: -1 | 1) {
    setSelectedMonday((prev) => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + delta * 7);
      return next;
    });
  }

  const mon = selectedMonday;
  const weekISO = `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`;
  const weekDays = getWeekDays(mon);
  const weekRange = `${weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  const { data: summary, isLoading } = useWeeklySummary(weekISO);
  const submitWeek = useSubmitWeek();

  const entries = summary?.entries ?? [];
  const total = summary?.total ?? 0;
  const billable = summary?.billable ?? 0;
  const overhead = summary?.overhead ?? 0;
  const pct = summary?.pct;
  const flaggedCount = summary?.flaggedCount ?? 0;

  const hasDrafts = entries.some((e) => e.status === "draft");
  const hasFlagged = flaggedCount > 0;
  const draftEntries = entries.filter((e) => e.status === "draft");
  const draftBillable = draftEntries
    .filter((e) => e.type === "billable")
    .reduce((s, e) => s + e.hours, 0);
  const draftOverhead = draftEntries
    .filter((e) => e.type === "overhead")
    .reduce((s, e) => s + e.hours, 0);

  const now = new Date();
  const hr = now.getHours();
  const greet = hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";
  const firstName = session?.user?.name?.split(" ")[0] ?? "there";

  function handleLogTime() {
    router.navigate({ to: "/dashboard/log-time" });
  }

  function handleEditEntry(entry: TimeEntry) {
    router.navigate({ to: "/dashboard/log-time", search: { edit: entry.id } });
  }

  function handleConfirmSubmit() {
    submitWeek.mutate(weekISO, {
      onSuccess: () => setConfirmSubmit(false),
    });
  }

  const byDay = weekDays.map((d) => {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { date: d, iso, entries: entries.filter((e) => e.entryDate === iso) };
  });

  const hasAnyEntries = byDay.some((d) => d.entries.length > 0);

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar__crumbs">
          <span>Engineering</span>
          <ChevronRight size={14} />
          <strong>Dashboard</strong>
        </div>
        <div className="topbar__actions" />
      </div>

      {/* Page content */}
      <div className="page">
        {/* Page header */}
        <div className="page-header">
          <div>
            <div className="page-header__eyebrow">
              <Calendar size={14} /> Week of {weekRange}
              <button
                type="button"
                className="icon-btn"
                title="Previous week"
                onClick={() => navigateWeek(-1)}
                style={{ marginLeft: 8 }}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                className="icon-btn"
                title="Next week"
                onClick={() => navigateWeek(1)}
                disabled={isCurrentWeek}
              >
                <ChevronRight size={14} />
              </button>
            </div>
            <h1 className="page-header__title">
              {greet}, {firstName}
            </h1>
            <p className="page-header__sub">
              {hasFlagged ? (
                <>
                  {`You have `}
                  <strong style={{ color: "var(--danger)" }}>
                    {flaggedCount} flagged {flaggedCount === 1 ? "entry" : "entries"}
                  </strong>
                  {` to resolve before submitting.`}
                </>
              ) : hasDrafts ? (
                `You have ${draftEntries.length} draft ${draftEntries.length === 1 ? "entry" : "entries"} ready to submit.`
              ) : (
                "Everything's submitted. Nice work."
              )}
            </p>
          </div>
        </div>

        {/* KPI row */}
        {!isLoading && (
          <div
            className="kpi-row"
            style={hasFlagged ? { gridTemplateColumns: "repeat(5, 1fr)" } : undefined}
          >
            <Kpi label="Total Hours This Week" value={formatHours(total)} unit="hrs" />
            <Kpi
              label="Billable"
              value={formatHours(billable)}
              unit="hrs"
              accent="var(--green-primary)"
            />
            <Kpi label="Overhead" value={formatHours(overhead)} unit="hrs" accent="var(--amber)" />
            <Kpi
              label="Billable %"
              value={pct === null || pct === undefined ? "—" : `${pct}%`}
              accent="var(--blue)"
            />
            {hasFlagged && <Kpi label="Flagged" value={flaggedCount} flagged />}
          </div>
        )}

        {/* Weekly entry table */}
        <div className="card" style={{ marginTop: 8 }}>
          <div className="card__header">
            <h2 className="h-section">This Week&apos;s Entries</h2>
            <div className="row-center t-small t-muted">
              <Info size={14} /> Hover a row for actions
            </div>
          </div>

          <div className="entry-table">
            {isLoading ? (
              <div style={{ padding: "32px 24px" }}>
                <div className="skeleton-row" style={{ height: 18, marginBottom: 12 }} />
                <div
                  className="skeleton-row"
                  style={{ height: 18, marginBottom: 12, width: "80%" }}
                />
                <div className="skeleton-row" style={{ height: 18, width: "60%" }} />
              </div>
            ) : !hasAnyEntries ? (
              <EmptyState />
            ) : (
              byDay.map((d) =>
                d.entries.length > 0 ? (
                  <DayGroup
                    key={d.iso}
                    date={d.date}
                    entries={d.entries}
                    onEdit={handleEditEntry}
                  />
                ) : null,
              )
            )}
          </div>

          <SubmitBar
            hasDrafts={hasDrafts}
            hasFlagged={hasFlagged}
            draftCount={draftEntries.length}
            onClick={() => setConfirmSubmit(true)}
            onLogTime={handleLogTime}
          />
        </div>
      </div>

      {/* Submit modal */}
      <SubmitModal
        open={confirmSubmit}
        onClose={() => setConfirmSubmit(false)}
        draftCount={draftEntries.length}
        draftBillable={draftBillable}
        draftOverhead={draftOverhead}
        weekRange={weekRange}
        onConfirm={handleConfirmSubmit}
        isPending={submitWeek.isPending}
      />
    </>
  );
}
